import React, { useCallback } from 'react';
import styles from './RewardPromoScreen.scss';
import { appActions } from '~/Store/appSlice';
import { useDispatch } from 'react-redux';
import useKeyDown, { KEYS_CODE } from '~/hooks/useKeyDown';
import { playButtonClickSound } from '@wg/web2clientapi/sound';
import ButtonEsc from '~/components/ButtonEsc/ButtonEsc';
import { t } from '~/utils/localization';
import WoWSEntity from '@wg/wows-entities/wrappers/react/WoWSEntity';
import Button from '~/components/Button/Button';
import Info from '~/components/Info/Info';
import { items } from '@wg/wows-entities/const';
import { getClientPath, isEmptyObject, isInGameBrowser, isMobileOrTabletWindow } from '~/utils/utils';
import classNames from 'classnames';

interface IRewardPromoScreen {
    data: {
        backgroundImage: string;
        backgroundColor: string;
        backgroundVideo: IVideo;
        showDisclaimer?: boolean;
        item: IItemCommonData;
        onClose?: () => void;
    };
}

const RewardPromoScreen = ({ data }: IRewardPromoScreen) => {
    const dispatch = useDispatch();
    const [isLoadedVideo, setLoadVideo] = React.useState(false);
    const close = useCallback(() => {
        data.onClose?.();
        dispatch(appActions.changeVisibilityPopup({ name: null }));
    }, [data]);

    useKeyDown(() => {
        playButtonClickSound();
        close();
    }, [KEYS_CODE.ESC]);

    const popupStyles = {
        '--background-color': `${data.backgroundColor || '#0B1017'}`,
    } as React.CSSProperties;

    let style: React.CSSProperties = {};
    if (data.backgroundImage) {
        style = {
            backgroundImage: `url(${data.backgroundImage})`,
        } as React.CSSProperties;
    }

    let text = t('You received a new item');
    if (data.item.type === items.VEHICLES) {
        text = t('You received a new ship');
    } else if (data.item.type === items.CREWS) {
        text = t('You received a new commander');
    }

    const classesVideoWrapper = classNames(styles.videoWrapper, {
        [styles.show]: isLoadedVideo,
    });

    return (
        <div className={styles.popup} style={popupStyles} key={`reward_promo_${data.item.type}_${data.item.identifier}`}>
            <div className={styles.background} style={style}>
                <div className={styles.mask}></div>
            </div>
            <div className={classesVideoWrapper}>
                {!isMobileOrTabletWindow && (
                    <>
                        {!isEmptyObject(data.backgroundVideo) && (
                            <div className={styles.videoBackground}>
                                <video
                                    autoPlay
                                    loop
                                    muted
                                    preload={'metadata'}
                                    playsInline
                                    onLoadedData={() => {
                                        setLoadVideo(true);
                                    }}
                                >
                                    {!!data.backgroundVideo?.clientVideoName?.trim().length && isInGameBrowser && (
                                        <source type={'video/webm'} src={getClientPath(`armory/${data.backgroundVideo.clientVideoName}`)} />
                                    )}
                                    <source src={data.backgroundVideo.webm} type="video/webm" />
                                    <source src={data.backgroundVideo.mp4} type="video/mp4" />
                                </video>
                                <div className={styles.videoBackgroundMask} />
                            </div>
                        )}
                    </>
                )}
            </div>
            <div className={styles.close}>
                <ButtonEsc onClick={close} />
            </div>
            <div className={styles.contentWrapper}>
                <div className={styles.header}>{t('Congratulations!')}</div>
                <div className={styles.footer}>
                    <div className={styles.footerTitle}>{text}</div>
                    <div className={styles.weWidget}>
                        <WoWSEntity
                            type={data.item.type}
                            id={data.item.identifier}
                            presentation={{
                                withTooltip: true,
                            }}
                        />
                    </div>
                    <div className={styles.buttons}>
                        <Button label={t('Закрыть')} onClick={close} />
                    </div>
                    {data.showDisclaimer && (
                        <div className={styles.disclaimer}>
                            <Info className={styles.infoIcon} />
                            <span>{t('Обратите внимание: внешний вид и описание объектов в Адмиралтействе могут иметь отличия от представленных в клиенте игры.')}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RewardPromoScreen;
